import { Button, Modal, ScrollArea, toast } from "@devhop/ui";
import { useQuery } from "@tanstack/react-query";
import { toPng } from "html-to-image";
import { DownloadIcon, Loader2Icon, PrinterIcon } from "lucide-react";
import QRCode from "qrcode";
import { useRef, useState } from "react";
import logo from "@/assets/favicon_io/android-chrome-192x192.png";
import { config } from "@/shared/lib/config";
import { formatDate, formatInLao, formatTime } from "@/shared/lib/date-time";
import {
  buildReceiptProfileSection,
  formatAssistInstructorBillHtml,
  formatAssistInstructorBillLine,
  formatBillAmount,
  RECEIPT_LOGO_STYLE_48MM,
  RECEIPT_PRINT_STYLES_48MM,
  RECEIPT_THERMAL_WIDTH_MM,
} from "@/shared/lib/receipt-print-48mm";
import type { ReceiptDetailDTO } from "../api/client";
import { billingApi } from "../api/client";
import { buildGroupedReceiptItemsHtml } from "../lib/build-grouped-receipt-items-html";
import {
  groupRoomBookingReceiptItems,
  hasPricedAssistReceiptItems,
  RoomBookingReceiptGroupCard,
} from "./RoomBookingReceiptGroupCard";

type ReceiptPreviewByApiDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  receiptId: string | null;
};

function buildReceiptContent(
  receipt: ReceiptDetailDTO,
  logoBase64?: string,
  profileSectionHtml?: string,
): string {
  const issuedAtFormatted = formatInLao(receipt.issuedAt, "dd/MM/yyyy HH:mm");
  const firstStudentItem = receipt.items.find(
    (i) => i.studentName != null || i.memberId != null,
  );
  const firstRoomBooker = receipt.items.find(
    (i) => i.itemType === "room_booking" && i.contactName?.trim(),
  );
  const customerName =
    firstStudentItem?.studentName ??
    firstRoomBooker?.contactName?.trim() ??
    "–";

  const itemsLines = buildGroupedReceiptItemsHtml(receipt.items);

  const payment = receipt.payment;
  const totalAmount = payment.amount;
  const hasDiscount =
    (payment.discountAmount ?? 0) > 0 &&
    (payment.originalAmount ?? payment.amount) > payment.amount;
  const originalAmount = payment.originalAmount ?? payment.amount;

  const logoImg =
    logoBase64 &&
    `<img src="${logoBase64}" alt="Logo" style="${RECEIPT_LOGO_STYLE_48MM}" />`;

  const discountHtml =
    hasDiscount && originalAmount > totalAmount
      ? `
    <div class="bill-discount">
      <div class="bill-discount-line">ຍອດເງິນ: ${formatBillAmount(originalAmount)} ກີບ</div>
      <div class="bill-discount-line discount-amount">ສ່ວນຫຼຸດ${
        payment.discountType === "percentage" && payment.discountValue != null
          ? ` (${payment.discountValue}%)`
          : payment.discountType === "fixed" && payment.discountValue != null
            ? ` (${formatBillAmount(payment.discountValue)} ກີບ)`
            : ""
      }: -${formatBillAmount(payment.discountAmount ?? 0)} ກີບ</div>
    </div>`
      : "";

  const tagline = config.billFooterTagline ?? "Keep Dancing, Keep Moving!";
  const address = config.brandAddress ?? "VIENTIANE CENTER, 4th floor";
  const addressLo = config.brandAddressLo ?? "ວຽງຈັນເຊັນເຕີ, ຊັ້ນ4";

  return `
    <div class="bill-header">
      ${logoImg ?? ""}
      <div class="bill-brand">${config.brandName.toUpperCase()}</div>
      <div class="bill-address">${addressLo}</div>
      <div class="bill-address">${address}</div>
    </div>
    <hr class="bill-sep" />

    <div class="bill-line">ເລກທີໃບ: ${receipt.receiptNo}</div>
    <div class="bill-line">ຊື່: ${customerName}</div>
    <div class="bill-line">ວັນທີ: ${issuedAtFormatted}</div>
    <hr class="bill-sep" />

    <div class="bill-items">${itemsLines}</div>
    <hr class="bill-sep" />
    ${discountHtml}

    <div class="bill-total-section">
      <div class="bill-total-label">ຍອດລວມທັງໝົດ</div>
      <div class="bill-total-amount">${formatBillAmount(totalAmount)} ກີບ</div>
    </div>
    <hr class="bill-sep" />
    ${profileSectionHtml ?? ""}
    ${profileSectionHtml ? '<hr class="bill-sep" />' : ""}
    <div class="bill-footer">
      <div class="bill-footer-tagline">${tagline}</div>
      <div class="bill-footer-thanks">ຂອບໃຈທີ່ໃຊ້ບໍລິການ</div>
    </div>
  `;
}

const RECEIPT_STYLES = RECEIPT_PRINT_STYLES_48MM;

async function loadLogoBase64(): Promise<string> {
  return new Promise<string>((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } else resolve("");
    };
    img.onerror = () => resolve("");
    img.src = logo;
  });
}

async function buildReceiptProfileHtml(
  receipt: ReceiptDetailDTO,
): Promise<string | undefined> {
  const firstStudentItem = receipt.items.find(
    (i) => i.studentName != null || i.memberId != null,
  );
  const memberId = firstStudentItem?.memberId ?? null;
  if (!memberId) return undefined;

  const origin = window.location.origin;
  const profileUrl = `${origin}/public/student/${encodeURIComponent(memberId)}`;
  const qrDataUrl = await QRCode.toDataURL(profileUrl, {
    width: 100,
    margin: 1,
  });
  return buildReceiptProfileSection(memberId, profileUrl, qrDataUrl);
}

function waitForImages(root: ParentNode): Promise<void> {
  const images = Array.from(root.querySelectorAll("img"));
  if (images.length === 0) return Promise.resolve();
  return Promise.all(
    images.map(
      (img) =>
        new Promise<void>((resolve) => {
          if (img.complete) {
            resolve();
            return;
          }
          img.onload = () => resolve();
          img.onerror = () => resolve();
        }),
    ),
  ).then(() => undefined);
}

function triggerPngDownload(dataUrl: string, filename: string) {
  const anchor = document.createElement("a");
  anchor.href = dataUrl;
  anchor.download = filename;
  anchor.click();
}

/** ພິມໃບເກັບເງິນຈາກ DTO (ດຶງ API ກ່ອນໃຊ້ printReceiptById) */
export async function printReceiptDetail(
  receipt: ReceiptDetailDTO,
): Promise<void> {
  const [profileSectionHtml, logoBase64] = await Promise.all([
    buildReceiptProfileHtml(receipt),
    loadLogoBase64(),
  ]);

  const printWindow = window.open("", "_blank");
  if (!printWindow) return;

  const content = buildReceiptContent(
    receipt,
    logoBase64 || undefined,
    profileSectionHtml,
  );

  printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>ໃບເກັບເງິນ - ${receipt.receiptNo}</title>
          <style>${RECEIPT_STYLES}</style>
        </head>
        <body>${content}</body>
      </html>
    `);

  printWindow.document.close();
  setTimeout(() => {
    printWindow.print();
    printWindow.close();
  }, 250);
}

/**
 * ดาวน์โหลดใบเก็บเงินเป็น PNG ความกว้างเท่าพิมพ์ thermal (55mm)
 */
export async function downloadReceiptDetailAsImage(
  receipt: ReceiptDetailDTO,
): Promise<void> {
  const [profileSectionHtml, logoBase64] = await Promise.all([
    buildReceiptProfileHtml(receipt),
    loadLogoBase64(),
  ]);

  const content = buildReceiptContent(
    receipt,
    logoBase64 || undefined,
    profileSectionHtml,
  );

  const host = document.createElement("div");
  host.setAttribute("aria-hidden", "true");
  host.style.cssText = [
    "position:fixed",
    "left:-10000px",
    "top:0",
    `width:${RECEIPT_THERMAL_WIDTH_MM}mm`,
    "pointer-events:none",
    "z-index:-1",
  ].join(";");

  const styleEl = document.createElement("style");
  // สไตล์เดิมอิง body — map ไปที่ .receipt-thermal-root สำหรับ capture
  styleEl.textContent = RECEIPT_STYLES.replaceAll(
    /\bbody\b/g,
    ".receipt-thermal-root",
  );

  const root = document.createElement("div");
  root.className = "receipt-thermal-root";
  root.innerHTML = content;

  host.appendChild(styleEl);
  host.appendChild(root);
  document.body.appendChild(host);

  try {
    await waitForImages(root);
    // ให้ layout settle ก่อน capture
    await new Promise((r) => requestAnimationFrame(() => r(undefined)));

    const dataUrl = await toPng(root, {
      backgroundColor: "#ffffff",
      pixelRatio: 3,
      cacheBust: true,
    });

    triggerPngDownload(
      dataUrl,
      `receipt-${receipt.receiptNo}-${RECEIPT_THERMAL_WIDTH_MM}mm.png`,
    );
  } finally {
    host.remove();
  }
}

export async function printReceiptById(receiptId: string): Promise<void> {
  const receipt = await billingApi.receipts.getById(receiptId);
  await printReceiptDetail(receipt);
}

export async function downloadReceiptById(receiptId: string): Promise<void> {
  const receipt = await billingApi.receipts.getById(receiptId);
  await downloadReceiptDetailAsImage(receipt);
}
export function ReceiptPreviewByApiDialog({
  open,
  onOpenChange,
  receiptId,
}: ReceiptPreviewByApiDialogProps) {
  const printRef = useRef<HTMLDivElement>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const { data: receipt, isLoading } = useQuery({
    queryKey: ["billing", "receipt", receiptId ?? ""],
    queryFn: () => billingApi.receipts.getById(receiptId!),
    enabled: open && !!receiptId,
  });

  const handlePrint = async () => {
    if (!receipt) return;
    await printReceiptDetail(receipt);
  };

  const handleDownloadImage = async () => {
    if (!receipt || isDownloading) return;
    setIsDownloading(true);
    try {
      await downloadReceiptDetailAsImage(receipt);
      toast.success("ດາວໂຫຼດຮູບໃບເກັບເງິນແລ້ວ");
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "ດາວໂຫຼດຮູບບໍ່ສຳເລັດ",
      );
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Modal open={open} onOpenChange={onOpenChange} title="ພິມໃບເກັບເງິນ" size="lg">
      <ScrollArea className="max-h-[80vh] pr-2">
        <div className="space-y-6">
          {isLoading && !receipt ? (
            <div className="flex items-center justify-center py-12">
              <Loader2Icon className="size-8 animate-spin text-muted-foreground" />
            </div>
          ) : !receiptId ? (
            <p className="py-8 text-center text-muted-foreground text-sm">
              ບໍ່ມີໃບເກັບເງິນ
            </p>
          ) : receipt ? (
            <div
              ref={printRef}
              className="mx-auto max-w-2xl rounded-lg border bg-white p-10 shadow-lg"
            >
              <div className="mb-8 border-red-600 border-b-3 pb-6 text-center">
                <img
                  src={logo}
                  alt="Logo"
                  className="mx-auto mb-4 size-20 rounded-lg object-contain"
                />
                <h1 className="mb-2 font-bold text-3xl text-gray-900">
                  {config.brandName}
                </h1>
                <h2 className="mb-1 font-semibold text-gray-600 text-lg uppercase tracking-wide">
                  ໃບເກັບເງິນ
                </h2>
                <p className="font-medium text-gray-500 text-sm uppercase tracking-wider">
                  Receipt
                </p>
              </div>

              <div className="mb-8 flex justify-between rounded-lg bg-gray-50 p-4">
                <div>
                  <p className="mb-1 font-medium text-gray-500 text-xs uppercase">
                    ວັນທີ
                  </p>
                  <p className="font-semibold">
                    {formatDate(receipt.issuedAt)}
                  </p>
                </div>
                <div className="text-right">
                  <p className="mb-1 font-medium text-gray-500 text-xs uppercase">
                    ເລກທີ
                  </p>
                  <p className="font-mono font-semibold">{receipt.receiptNo}</p>
                </div>
              </div>

              {(() => {
                const firstStudentItem = receipt.items.find(
                  (i) => i.studentName != null || i.memberId != null,
                );
                return (
                  firstStudentItem &&
                  (firstStudentItem.studentName ||
                    firstStudentItem.memberId) && (
                    <div className="mb-8">
                      <p className="mb-4 border-gray-200 border-b-2 pb-2 font-bold text-gray-900 text-sm uppercase tracking-wide">
                        ຂໍ້ມູນນັກຮຽນ
                      </p>
                      <div className="space-y-3 text-sm">
                        {firstStudentItem.studentName && (
                          <div className="flex justify-between">
                            <span className="font-medium text-gray-600">
                              ຊື່:
                            </span>
                            <span className="font-semibold text-gray-900">
                              {firstStudentItem.studentName}
                            </span>
                          </div>
                        )}
                        {firstStudentItem.memberId && (
                          <div className="flex justify-between">
                            <span className="font-medium text-gray-600">
                              ເລກສະມາຊິກ:
                            </span>
                            <span className="font-semibold text-gray-900">
                              {firstStudentItem.memberId}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )
                );
              })()}

              <div className="mb-8">
                <p className="mb-4 border-gray-200 border-b-2 pb-2 font-bold text-gray-900 text-sm uppercase tracking-wide">
                  ລາຍການ
                </p>
                <div className="space-y-3">
                  {(() => {
                    const { roomBookingGroups, orphanAssistItems, otherItems } =
                      groupRoomBookingReceiptItems(receipt.items);
                    const showLegacyAssist = !hasPricedAssistReceiptItems(
                      receipt.items,
                    );

                    return (
                      <>
                        {roomBookingGroups.map((group) => (
                          <RoomBookingReceiptGroupCard
                            key={group.roomItem.id}
                            group={group}
                            showLegacyAssistNames={showLegacyAssist}
                          />
                        ))}
                        {orphanAssistItems.map((item) => (
                          <div
                            key={item.id}
                            className="flex justify-between border-gray-100 border-b py-3"
                          >
                            <div className="flex-1">
                              <div className="font-semibold text-gray-900">
                                {item.description}
                              </div>
                              <div className="mt-2 text-gray-600 text-sm">
                                {item.quantity} ×{" "}
                                {item.unitPrice.toLocaleString("lo-LA")} ກີບ
                              </div>
                            </div>
                            <div className="ml-4 font-semibold text-gray-900">
                              {item.totalPrice.toLocaleString("lo-LA")} ກີບ
                            </div>
                          </div>
                        ))}
                        {otherItems.map((item) => (
                          <div
                            key={item.id}
                            className="flex justify-between border-gray-100 border-b py-3"
                          >
                            <div className="flex-1">
                              <div className="whitespace-pre-line font-semibold text-gray-900">
                                {item.description}
                              </div>
                              <div className="mt-2 space-y-1 text-gray-600 text-sm">
                                {item.startDate && item.expireDate && (
                                  <div>
                                    ຊ່ວງເວລາທີ່ໃຊ້ໄດ້:{" "}
                                    {formatDate(item.startDate)} –{" "}
                                    {formatDate(item.expireDate)}
                                  </div>
                                )}
                                {item.selectedClasses &&
                                  item.selectedClasses.length > 0 && (
                                    <div>
                                      {item.selectedClasses
                                        .map(
                                          (c) =>
                                            `${c.classTypeName} - ${c.levelName}`,
                                        )
                                        .join(", ")}
                                    </div>
                                  )}
                                {item.totalHours != null &&
                                  item.pricePerHour != null && (
                                    <div>
                                      {item.totalHours} ຊົ່ວໂມງ ×{" "}
                                      {item.pricePerHour.toLocaleString("lo-LA")}{" "}
                                      ກີບ/ຊົ່ວໂມງ
                                    </div>
                                  )}
                                {!item.description.includes("\n") &&
                                  item.roomName &&
                                  item.date && (
                                    <>
                                      <div>ຫ້ອງ: {item.roomName}</div>
                                      <div>
                                        ວັນທີ: {formatDate(item.date)}
                                        {item.startTime &&
                                          item.endTime &&
                                          ` · ເວລາ: ${formatTime(item.startTime)} – ${formatTime(item.endTime)}`}
                                      </div>
                                      {formatAssistInstructorBillLine(
                                        item.assistInstructorNames,
                                      ) ? (
                                        <div>
                                          {formatAssistInstructorBillLine(
                                            item.assistInstructorNames,
                                          )}
                                        </div>
                                      ) : null}
                                    </>
                                  )}
                                <div>
                                  ຈຳນວນ: {item.quantity} ×{" "}
                                  {item.unitPrice.toLocaleString("lo-LA")} ກີບ
                                </div>
                              </div>
                            </div>
                            <div className="ml-4 font-semibold text-gray-900">
                              {item.totalPrice.toLocaleString("lo-LA")} ກີບ
                            </div>
                          </div>
                        ))}
                      </>
                    );
                  })()}
                </div>
              </div>

              <div className="border-red-600 border-t-3 pt-6 text-right">
                <p className="mb-2 font-semibold text-gray-600 text-sm uppercase tracking-wide">
                  ຈຳນວນເງິນທີ່ຕ້ອງຊຳລະທັງໝົດ
                </p>
                {(() => {
                  const pm = receipt.payment;
                  const hasDisc =
                    (pm.discountAmount ?? 0) > 0 &&
                    (pm.originalAmount ?? pm.amount ?? 0) > (pm.amount ?? 0);
                  return hasDisc ? (
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">ຍອດເງິນ:</span>
                        <span>
                          {(pm.originalAmount ?? pm.amount ?? 0).toLocaleString(
                            "lo-LA",
                          )}{" "}
                          ກີບ
                        </span>
                      </div>
                      <div className="flex justify-between text-gray-600 text-sm">
                        <span>
                          ສ່ວນຫຼຸດ
                          {pm.discountType === "percentage" &&
                          pm.discountValue != null
                            ? ` (${pm.discountValue}%)`
                            : pm.discountType === "fixed" &&
                                pm.discountValue != null
                              ? ` (${(pm.discountValue ?? 0).toLocaleString("lo-LA")} ກີບ)`
                              : ""}
                          :
                        </span>
                        <span className="font-semibold">
                          -{(pm.discountAmount ?? 0).toLocaleString("lo-LA")} ກີບ
                        </span>
                      </div>
                      <div className="flex justify-between border-t pt-2">
                        <span className="font-semibold">ລາຄາສຸດທ້າຍ:</span>
                        <span className="font-bold text-4xl text-red-600">
                          {receipt.payment.amount.toLocaleString("lo-LA")} ກີບ
                        </span>
                      </div>
                    </div>
                  ) : (
                    <p className="font-bold text-4xl text-red-600">
                      {receipt.payment.amount.toLocaleString("lo-LA")} ກີບ
                    </p>
                  );
                })()}
              </div>

              <div className="mt-12 border-gray-200 border-t pt-6 text-center">
                <p className="mb-1 font-semibold text-gray-700 text-sm">
                  ຂອບໃຈທີ່ໃຊ້ບໍລິການ
                </p>
                <p className="text-gray-500 text-xs">
                  Thank you for your business
                </p>
              </div>
            </div>
          ) : null}

          {receipt && (
            <div className="sticky bottom-0 flex justify-end gap-3 border-t bg-white pt-4">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                ຍົກເລີກ
              </Button>
              <Button
                variant="outline"
                onClick={() => void handleDownloadImage()}
                disabled={isDownloading}
              >
                {isDownloading ? (
                  <Loader2Icon className="mr-2 size-4 animate-spin" />
                ) : (
                  <DownloadIcon className="mr-2 size-4" />
                )}
                ດາວໂຫຼດຮູບ ({RECEIPT_THERMAL_WIDTH_MM}mm)
              </Button>
              <Button
                onClick={() => void handlePrint()}
                className="bg-red-600 hover:bg-red-700"
              >
                <PrinterIcon className="mr-2 size-4" />
                ພິມ
              </Button>
            </div>
          )}
        </div>
      </ScrollArea>
    </Modal>
  );
}
